/**
 * Site configuration — the single knob every child sibling reads to
 * decide which parent features to show / hide.
 *
 * Resolution order (first hit wins):
 *   1. URL query `?site=<slug>`     — preview mode inside the parent
 *   2. `REACT_APP_SITE_SOURCE` env   — set by the child's own Emergent app
 *   3. Hostname prefix / suffix      — kerala.socialpulse.com → "kerala"
 *   4. Default → "socialpulse"
 *
 * Each site's config carries just enough per-brand overrides
 * (allowed nav items, portal section allowlist, headline palette).
 * Everything else — auth, geographic data, AI, moderation — comes
 * from the parent unchanged.
 */

// Canonical site profiles. Keep the shape tight so every child
// declares the exact same fields — makes it trivial to spin up a new
// sibling: just add a row here.
export const SITE_PROFILES = {
  socialpulse: {
    slug:        "socialpulse",
    label:       "Social Pulse",
    // null hideNav → show every nav item
    hideNav:     null,
    // null portalSections → show every taxonomy section
    portalSections: null,
    defaultLang: "en",
    countryPref: null,
    accent:      "#7C3AED",
  },
  kerala: {
    slug:        "kerala",
    label:       "Kerala Pulse",
    // Kerala emphasises districts + tourism + community; drop the
    // items that are US-only or off-brand.
    hideNav:     ["family_tree", "civic_reports"],
    portalSections: null,
    defaultLang: "ml",
    countryPref: "india",
    accent:      "#0E7C55",
  },
  foodpulse: {
    slug:        "foodpulse",
    label:       "Food Pulse",
    // Food Pulse is a vertical: hide non-food sections at the nav
    // level and restrict Portal View to food-adjacent taxonomy.
    hideNav:     ["family_tree", "memorials", "housing", "sports", "education", "government"],
    portalSections: [
      "food",          // restaurants / markets / bakeries / farmers
      "directory",     // hospitals stays out — only food POIs surface
      "community",     // local food news / marketplace
      "business",      // markets / procurement
      "news-hub",      // food-adjacent news
      "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#EA580C",
  },
  indias: {
    slug:        "indias",
    label:       "Indias",
    // Indias.com is India-only. Hide US-specific directories.
    hideNav:     ["us_counties", "us_states"],
    portalSections: null,
    defaultLang: "hi",
    countryPref: "india",
    accent:      "#B45309",
  },
  keralatravels: {
    slug:        "keralatravels",
    label:       "Kerala Travels",
    // Hotel-booking sibling for Kerala tourism. Hide non-travel
    // sections at nav-level and lock the Portal View to
    // tourism-adjacent taxonomy.
    hideNav:     ["family_tree", "memorials", "housing", "civic_reports",
                  "us_counties", "us_states"],
    portalSections: [
      "tourism", "travel", "food", "directory",
      "business", "community", "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: "india",
    accent:      "#0E7C55",
  },
  indianfamilytree: {
    slug:        "indianfamilytree",
    label:       "Indian Family Tree",
    // Genealogy-focused sibling. Hide domain-irrelevant nav so the
    // homepage reads as a family-tree destination, not a general
    // portal. Family Tree stays front-and-centre.
    hideNav:     ["food_pulse", "civic_reports", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace", "sports"],
    portalSections: [
      "family", "memorials", "community", "directory",
      "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: "india",
    accent:      "#B25D38", // saffron
  },
  myfamilytreecare: {
    slug:        "myfamilytreecare",
    label:       "MyFamilyTree.care",
    // Heritage-archive sibling. Same nav footprint as IFT — this is a
    // global memorial/heritage brand. Data still lives in the shared
    // Social Pulse DB; child-site attribution is written via the
    // ``X-Site-Source`` header on every write.
    hideNav:     ["food_pulse", "civic_reports", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace", "sports"],
    portalSections: [
      "family", "memorials", "community", "directory",
      "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#0E7C55", // heritage green
  },
  naturepulse: {
    slug:        "naturepulse",
    label:       "Nature Pulse",
    // NaturePulse.net — observing, documenting, and sharing information
    // about nature, agriculture, biodiversity and environmental change.
    // Farmers Pulse lives inside NaturePulse.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate"],
    portalSections: [
      "nature", "agriculture", "food", "community",
      "directory", "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#0E7C55", // botanical green
  },
  sellnbuy: {
    slug:        "sellnbuy",
    label:       "SellnBuy",
    // SocialPulse Local marketplace — global hyperlocal buy · sell ·
    // share · hire · help · volunteer · rescue platform. Data is
    // unified with SocialPulse; SellnBuy is the primary consumer.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate"],
    portalSections: [
      "marketplace", "services", "tasks", "community",
      "food", "directory", "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#1F6FEB", // trust blue
  },
  socialcapital: {
    slug:        "socialcapital",
    label:       "Social Capital",
    // SocialCapital.cc — the ecosystem's independent trust &
    // reputation ledger. Members submit or receive verified activities
    // from participating organizations; the central engine calculates
    // recognition. External API lives at /api/sc/*.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace"],
    portalSections: [
      "passport", "activities", "organizations", "leaderboard",
      "api-docs", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#8B5CF6", // trust violet
  },
  worldpulse: {
    slug:        "worldpulse",
    label:       "WorldPulse",
    // WorldPulse.pro — Global Geographic Portal Platform. Owns
    // PLACE (countries, states, districts, cities, villages) via a
    // thin adapter over the existing `locations` collection. Sister
    // portals `indias.com` and `usapulse.net` render the same
    // codebase with country-scoped branding (see profiles below).
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace"],
    portalSections: [
      "world", "countries", "regions", "search", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#4f46e5", // network indigo
  },
  newspulse: {
    slug:        "newspulse",
    label:       "TheNewsPulse",
    // TheNewsPulse.pro — Central News Intelligence & Distribution.
    // Collects once, understands once, distributes to every sister site.
    // Public UI shows events, categories, geo/entity feeds, and
    // "What Changed Today". Admin lives under /admin/newspulse.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace"],
    portalSections: [
      "home", "topics", "world", "what-changed", "search", "about",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#0f172a", // editorial slate — trust + gravitas
  },
  blogspulse: {
    slug:        "blogspulse",
    label:       "BlogsPulse",
    // blogs.socialpulse.com (temporary) → blogspulse.pro (once SSL
    // clears). Publisher canonical destination — hubs, writer,
    // notes timeline, admin. Sister-site widgets embed content from
    // here via /api/publisher/v1/widget/feed.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace"],
    portalSections: ["home", "notes", "topics", "authors", "about"],
    defaultLang: "en",
    countryPref: null,
    accent:      "#f59e0b", // amber — same accent as note cards
  },
  chatspulse: {
    slug:        "chatspulse",
    label:       "ChatsPulse",
    // chats.socialpulse.com (temporary) → chatspulse.pro (once SSL
    // clears). Unified discussion forum canonical destination. Every
    // event / article / location / topic gets a permanent
    // thread URL here, and sister sites embed the widget.
    hideNav:     ["family_tree", "memorials", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace"],
    portalSections: ["home", "hot", "recent", "topics", "about"],
    defaultLang: "en",
    countryPref: null,
    accent:      "#2563eb", // conversational blue
  },
  indias: {
    slug:        "indias",
    label:       "Indias.com",
    // Indias.com — India country portal on the WorldPulse engine.
    // Reads from the same `locations` collection; branded home + nav
    // scoped to India only. Hides sister-app nav that isn't Indian.
    hideNav:     ["family_tree", "memorials", "us_counties", "us_states",
                  "real_estate", "marketplace"],
    portalSections: ["overview", "states", "districts", "cities", "villages", "contact"],
    defaultLang: "en",
    countryPref: "india",
    accent:      "#059669", // Indian green
    countryIso:  "IN",
    portalDomain: "indias.com",
  },
  usapulse: {
    slug:        "usapulse",
    label:       "USAPulse",
    // USAPulse.net — USA country portal on the WorldPulse engine.
    // Same reuse model as Indias.com; scoped to United States only.
    hideNav:     ["family_tree", "memorials", "real_estate", "marketplace"],
    portalSections: ["overview", "states", "counties", "cities", "contact"],
    defaultLang: "en",
    countryPref: "usa",
    accent:      "#2563eb", // USA blue
    countryIso:  "US",
    portalDomain: "usapulse.net",
  },
};

// Hostname → slug overrides that don't follow the `<slug>.com`
// pattern (custom domains, staging URLs, etc.). Wins over the
// prefix/suffix heuristics below.
const HOST_ALIASES = {
  "hotel-booking-beta.emergent.host": "keralatravels",
  // The `.care` TLD needs an explicit alias — the prefix/suffix
  // heuristic below only matches `<slug>.com`.
  "myfamilytree.care":       "myfamilytreecare",
  "www.myfamilytree.care":   "myfamilytreecare",
  // FoodPulse is deployed on the `.net` apex — the `<slug>.com`
  // heuristic misses it, so we alias explicitly.
  "foodpulse.net":       "foodpulse",
  "www.foodpulse.net":   "foodpulse",
  // NaturePulse lives on `.net` too — alias explicitly.
  "naturepulse.net":     "naturepulse",
  "www.naturepulse.net": "naturepulse",
  // SellnBuy — the SocialPulse Local marketplace runs on its own
  // domain but shares this codebase + DB. Alias both apex + www.
  "sellnbuy.com":        "sellnbuy",
  "www.sellnbuy.com":    "sellnbuy",
  // SocialCapital.cc — independent trust & reputation ledger.
  "socialcapital.cc":     "socialcapital",
  "www.socialcapital.cc": "socialcapital",
  // WorldPulse — Global Geographic Portal Platform, `.pro` TLD.
  "worldpulse.pro":       "worldpulse",
  "www.worldpulse.pro":   "worldpulse",
  // TheNewsPulse — Central News Intelligence + Distribution, `.pro` TLD.
  "thenewspulse.pro":       "newspulse",
  "www.thenewspulse.pro":   "newspulse",
  "newspulse.pro":          "newspulse",
  "www.newspulse.pro":      "newspulse",
  // Subdomain of the SSO apex — served by the same monolith today,
  // will re-point at a dedicated Emergent fork later. Keeps NewsPulse
  // reachable while `.pro` SSL provisioning remains blocked at
  // Cloudflare's SSL-for-SaaS layer.
  "news.socialpulse.com":   "newspulse",
  // BlogsPulse — Publisher canonical destination. NEW primary host is
  // socialpulse.blog (a purpose-bought `.blog` TLD). The old
  // blogs.socialpulse.com hostname stays aliased so bookmarks and
  // widget embeds keep resolving during the DNS cutover.
  "socialpulse.blog":       "blogspulse",
  "www.socialpulse.blog":   "blogspulse",
  "blogs.socialpulse.com":  "blogspulse",
  "blogspulse.pro":         "blogspulse",
  "www.blogspulse.pro":     "blogspulse",
  // Legacy Publisher host — aliased so any bookmark keeps working
  // until we ship the 301 redirect middleware.
  "blog.socialpulse.com":   "blogspulse",
  // ChatsPulse — unified forum canonical destination. Primary host is
  // chats.socialpulse.blog once DNS lands; .com sub keeps working.
  "chats.socialpulse.blog": "chatspulse",
  "chats.socialpulse.com":  "chatspulse",
  "chatspulse.pro":         "chatspulse",
  "www.chatspulse.pro":     "chatspulse",
  // NewsPulse subdomain on the .blog apex — primary host once DNS lands.
  "news.socialpulse.blog":  "newspulse",
  // Indias.com — India-branded country portal on the WorldPulse engine.
  "indias.com":           "indias",
  "www.indias.com":       "indias",
  // USAPulse.net — USA-branded country portal on the WorldPulse engine.
  "usapulse.net":         "usapulse",
  "www.usapulse.net":     "usapulse",
  // Indian Family Tree matches via the .com heuristic already; the
  // www. variant just needs the explicit alias to skip the heuristic.
  "www.indianfamilytree.com":  "indianfamilytree",
  // Preview hosts used for QA prior to production deploys.
  "foodpulse.preview.emergent.host":       "foodpulse",
  "indianfamilytree.preview.emergent.host": "indianfamilytree",
};

const ALLOWED = new Set(Object.keys(SITE_PROFILES));

function fromHostname(host) {
  const h = (host || "").toLowerCase();
  if (HOST_ALIASES[h]) return HOST_ALIASES[h];
  for (const slug of ALLOWED) {
    // Prefix (kerala.socialpulse.com) or apex (foodpulse.net,
    // indias.com) both count. `socialpulse.com` itself falls
    // through to the default profile.
    if (slug === "socialpulse") continue;
    if (h.startsWith(`${slug}.`)) return slug;
    if (h === `${slug}.com` || h.endsWith(`.${slug}.com`)) return slug;
  }
  return null;
}

/** Resolve the active site slug at render time. */
export function resolveSiteSlug({ queryParam, hostname } = {}) {
  const q = (queryParam || "").toLowerCase();
  if (q && ALLOWED.has(q)) return q;

  const envSite = (process.env.REACT_APP_SITE_SOURCE || "").toLowerCase();
  if (envSite && ALLOWED.has(envSite)) return envSite;

  const fromHost = fromHostname(hostname || (typeof window !== "undefined" ? window.location.hostname : ""));
  if (fromHost) return fromHost;

  return "socialpulse";
}

/** Full profile for a site slug — always returns a non-null profile. */
export function siteProfile(slug) {
  return SITE_PROFILES[(slug || "").toLowerCase()] || SITE_PROFILES.socialpulse;
}

/** Convenience predicates used across the nav/portal machinery. */
export function isNavHidden(profile, key) {
  return Array.isArray(profile?.hideNav) && profile.hideNav.includes(key);
}

export function isPortalSectionAllowed(profile, sectionSlug) {
  if (!profile?.portalSections) return true;
  return profile.portalSections.includes(sectionSlug);
}
